// ============================================================
// ExpressParamsAndBody.jsx
//
// A branded, responsive course content page for the
// "Request Data: Params, Query & Body" lesson.
//
// Author: ProDev Linux Series (Backend Branch)
// Features: Comparative analysis of data capture methods,
//           interactive code blocks, and Dashboard navigation.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import DataObjectIcon         from "@mui/icons-material/DataObject";
import LinkIcon              from "@mui/icons-material/Link";
import InputIcon             from "@mui/icons-material/Input";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import HelpOutlineIcon       from "@mui/icons-material/HelpOutline";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressParamsAndBody() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Data Ingestion · Module 07"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Request <span>Data Inputs</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Express provides three primary ways to capture client data. Choosing the right one depends on whether you are identifying a resource, filtering a list, or sending complex payloads.
                    </Typography>
                </Box>

                {/* 1. ROUTE PARAMETERS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LinkIcon />} label="1. Route Parameters (req.params)" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Used for identifying specific resources via the URL path. These are mandatory segments defined by a colon (<code style={{color: BRAND.white}}>:</code>).
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.get('/products/:productId', (req, res) => {", comment: "The URL is /products/123" },
                        { cmd: "  const { productId } = req.params;", comment: "productId = '123'" },
                        { cmd: "  res.send(`ID: ${productId}`);", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 2. QUERY PARAMETERS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HelpOutlineIcon />} label="2. Query Parameters (req.query)" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Optional key-value pairs following a <code style={{color: BRAND.white}}>?</code> in the URL. Best for search, sorting, and filtering.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.get('/search', (req, res) => {", comment: "The URL is /search?q=nodejs" },
                        { cmd: "  const { q } = req.query;", comment: "q = 'nodejs'" },
                        { cmd: "  res.send(`Searching for: ${q}`);", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 3. REQUEST BODY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<InputIcon />} label="3. Request Body (req.body)" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Used for sending large JSON objects or form data. Requires the <code style={{color: BRAND.white}}>express.json()</code> middleware to parse the payload.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.use(express.json());", comment: "Crucial: Enable JSON parsing" },
                        { cmd: "", comment: "" },
                        { cmd: "app.post('/users', (req, res) => {", comment: "Typically POST or PUT" },
                        { cmd: "  const { name, email } = req.body;", comment: "Access JSON fields" },
                        { cmd: "  res.json({ message: `User ${name} created` });", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* SUMMARY TABLE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<DataObjectIcon />} label="Quick Reference" />
                    <Grid container spacing={2}>
                        {[
                            { type: "Route Params", url: "/users/:id", access: "req.params", use: "Identification" },
                            { type: "Query Params", url: "?sort=asc", access: "req.query", use: "Filtering/Search" },
                            { type: "Body", url: "N/A (Hidden)", access: "req.body", use: "Complex Payloads" },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.type}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 15, mb: 1 }}>{item.type}</Typography>
                                    <Typography sx={{ color: BRAND.white, fontFamily: 'monospace', fontSize: 12 }}>{item.url}</Typography>
                                    <Typography sx={{ color: BRAND.goldBright, fontFamily: 'monospace', fontSize: 12, my: 0.5 }}>{item.access}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>Use for: {item.use}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Learning Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Capture dynamic values from URL paths via req.params",
                            "Extract search and filter strings using req.query",
                            "Ingest JSON payloads using req.body and middleware",
                            "Understand when to use Params vs Query vs Body",
                            "Configure Express to parse various client-side data formats",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Route params are <strong>required</strong> parts of the URL. If the user hits <code style={{ color: BRAND.goldBright }}>/products/</code> without an ID, Express won't match the <code style={{ color: BRAND.goldBright }}>/products/:productId</code> route!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
